import axios from "axios";
import toast from "react-hot-toast";

// API Base URL - in development, Vite proxy will handle /api routes
export const API_BASE_URL =
  process.env.NODE_ENV === "production" ? window.location.origin : "";

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json", // Tell Flask to return JSON
  },
  withCredentials: true, // Include cookies for session management
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add any auth headers if needed
    const token = localStorage.getItem("auth_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Handle specific error cases
      switch (error.response.status) {
        case 401:
          // Unauthorized - redirect to login
          window.location.href = "/login";
          break;
        case 403:
          toast.error("You do not have permission to perform this action");
          break;
        case 404:
          toast.error("Resource not found");
          break;
        case 429:
          toast.error("Too many requests. Please try again later.");
          break;
        case 500:
          // Don't show generic toast for 500 - let components show detailed backend errors
          break;
        default:
          toast.error(error.response.data.message || "An error occurred");
      }
    } else if (error.request) {
      toast.error("Network error. Please check your connection.");
    } else {
      toast.error("An unexpected error occurred");
    }
    return Promise.reject(error);
  },
);

export default api;

// API Endpoints
export const endpoints = {
  // Auth
  login: "/login",
  logout: "/logout",
  profile: "/profile",
  updateProfile: "/profile/update",

  // Dashboard
  dashboard: "/dashboard",

  // Campaigns
  campaigns: "/my-campaigns",
  createCampaign: "/campaign/new",
  campaignDetail: (id: string) => `/campaign/${id}`,
  deleteCampaign: (id: string) => `/campaign/${id}/delete`,
  useCampaign: (id: string) => `/campaign/${id}/use`,
  campaignEmails: (id: string) => `/campaign/${id}/emails`,
  campaignAnalytics: (id: string) => `/campaign/${id}/analytics`,
  sendAllEmails: (id: string) => `/campaign/${id}/send-all`,
  generateEmails: (id: string) => `/campaign/${id}/generate`,

  // Email Accounts
  emailAccounts: "/email_accounts",
  connectEmailAccount: "/email_accounts/connect",
  connectSmtpAccount: "/api/email_accounts/connect-smtp",
  validateSmtpCredentials: "/api/email_accounts/validate-credentials",
  providerConfigs: "/api/email_accounts/provider-configs",
  emailAccountDetail: (id: string) => `/email_accounts/${id}`,
  deleteEmailAccount: (id: string) => `/email_accounts/${id}/delete`,
  reconnectEmailAccount: (id: string) => `/email_accounts/${id}/reconnect`,
  removeDuplicateAccount: (id: string) =>
    `/email_accounts/${id}/remove-duplicate`,
  updateEmailAccountSettings: (accountUuid: string) =>
    `/api/email_accounts/${accountUuid}/update-settings`,

  // Email Account Warmup
  enableWarmup: (id: string) => `/email_accounts/${id}/warmup/enable`,
  disableWarmup: (id: string) => `/email_accounts/${id}/warmup/disable`,
  pauseWarmup: (id: string) => `/email_accounts/${id}/warmup/pause`,
  resumeWarmup: (id: string) => `/email_accounts/${id}/warmup/resume`,
  checkSpamFolder: (id: string) => `/email_accounts/${id}/warmup/check-spam`,
  warmupSettings: (id: string) => `/email_accounts/${id}/warmup/settings`,
  warmupMetrics: (id: string) => `/email_accounts/${id}/warmup/metrics`,
  updateEmailAccountSignature: (id: string) =>
    `/email_accounts/${id}/signature`,

  // Master Inbox
  masterInbox: "/master_inbox",
  syncInbox: "/sync_inbox",
  messageDetail: (id: number) => `/message/${id}`,
  markMessageRead: (id: number) => `/message/${id}/mark_read`,
  replyToMessage: (id: number) => `/message/${id}/reply`,

  // Email Operations
  sendEmail: (emailId: number) => `/email/${emailId}/send`,
  sendEmailUnified: (emailId: number) => `/api/email/${emailId}/send-unified`,
  queueCampaignEmails: (campaignId: number) =>
    `/api/campaign/${campaignId}/queue-all`,
  editEmail: (campaignId: string, emailId: string) =>
    `/campaign/${campaignId}/email/${emailId}/edit`,

  // Tracking
  trackOpen: (trackingId: string) => `/t/${trackingId}.png`,
  trackClick: (linkId: string) => `/l/${linkId}`,

  // Email Verification
  verifyEmails: (campaignId: string) => `/campaign/${campaignId}/verify-emails`,
  verifyEmailsProgress: (campaignId: string) =>
    `/campaign/${campaignId}/verify-emails/progress`,
  verifyEmailsStatus: (campaignId: string) =>
    `/api/campaign/${campaignId}/verify-emails/status`,

  // Analytics
  analyticsCustomers: (startDate: string, endDate: string) =>
    `/api/analytics/customers?start_date=${startDate}&end_date=${endDate}`,
  analyticsCampaigns: (
    startDate: string,
    endDate: string,
    groupBy: "day" | "week" | "month" = "day",
  ) =>
    `/api/analytics/campaigns?start_date=${startDate}&end_date=${endDate}&group_by=${groupBy}`,

  // Admin
  adminCustomers: "/api/admin/customers",
  adminCustomerDetail: (customerId: string | number) =>
    `/api/admin/customers/${customerId}`,
  adminCampaigns: "/api/admin/campaigns",
  adminCampaignDetail: (campaignId: string | number) =>
    `/api/admin/campaigns/${campaignId}`,
  adminConfigurations: "/api/admin/configurations",
  subscriptionCurrent: "/api/subscription/current",
};
